import type React from "react";
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  type SxProps,
  type Theme,
  useTheme,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { colors } from "../constants/colors";
import DisabledButtonTooltip from "./common/DisabledButtonTooltip";

interface SidebarMenuItemProps {
  to?: string;
  icon?: React.ReactNode;
  text: string;
  selected?: boolean;
  isSubmenu?: boolean;
  disabled?: boolean;
  disabledHint?: React.ReactNode;
  textProps?: {
    fontSize?: string;
  };
  sx?: SxProps<Theme>;
  onClick?: () => void;
  onNavigate?: () => void;
  "data-testid"?: string;
}

const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  to,
  icon,
  text,
  selected,
  isSubmenu,
  disabled = false,
  disabledHint,
  textProps,
  sx,
  onClick,
  onNavigate,
  "data-testid": testId,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleClick = () => {
    if (disabled) {
      return;
    }
    if (onClick) {
      onClick();
    } else if (to) {
      navigate(to);
      onNavigate?.();
    }
  };

  const baseStyles: SxProps<Theme> = {
    "&.Mui-selected": {
      backgroundColor: theme.sidebar.selected,
      "&:hover": {
        backgroundColor: theme.sidebar.selected,
      },
      "& .MuiListItemText-primary": {
        color: `${theme.palette.primary.main} !important`,
      },
    },
    "&:hover": {
      backgroundColor: theme.sidebar.hover,
    },
    "& .MuiTouchRipple-root": {
      color: colors.rippleEffect,
    },
  };

  const submenuStyles: SxProps<Theme> = {
    ...baseStyles,
    pl: 4,
    py: 0.5,
  };

  const button = (
    <ListItemButton
      selected={!disabled && (selected || (to ? location.pathname === to : false))}
      disabled={disabled}
      onClick={handleClick}
      data-testid={testId}
      sx={{
        minHeight: 48,
        px: 2.5,
        ...(isSubmenu ? submenuStyles : baseStyles),
        ...sx,
      }}
    >
      {icon && <ListItemIcon sx={{ minWidth: 40 }}>{icon}</ListItemIcon>}
      <ListItemText
        primary={text}
        primaryTypographyProps={{
          fontSize: textProps?.fontSize || (isSubmenu ? "0.875rem" : "1rem"),
          fontWeight: selected ? 600 : 400,
        }}
      />
    </ListItemButton>
  );

  return (
    <DisabledButtonTooltip title={disabledHint ?? ""} disabled={disabled}>
      {button}
    </DisabledButtonTooltip>
  );
};

export default SidebarMenuItem;
